import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Home, ChevronDown, Menu, X, Crown, LogOut, LayoutDashboard } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { SPORTS } from "@/lib/content";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const PICKS = SPORTS.map((s) => ({ label: s.label, to: `/${s.key}` }));
const ODDS = SPORTS.map((s) => ({ label: `${s.league} Odds`, to: `/${s.key}#odds` }));
const TOOLS = [
  { label: "NFL & NCAA Schedule", to: "/schedule" },
  { label: "Betting Education", to: "/education" },
  { label: "The Smasher Strategy", to: "/strategy" },
  { label: "Get The Ebook", to: "/ebook" },
  { label: "Responsible Wagering", to: "/responsible" },
];

function NavDropdown({ label, items }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="flex items-center gap-1 text-sm font-semibold tracking-wide text-slate-200 hover:text-smasher-gold transition-colors outline-none"
        data-testid={`nav-${label.toLowerCase()}`}
      >
        {label} <ChevronDown className="w-4 h-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="bg-smasher-panel border border-smasher-gold/20 text-slate-200">
        {items.map((it) => (
          <DropdownMenuItem key={it.to + it.label} asChild className="cursor-pointer focus:bg-smasher-gold/15 focus:text-smasher-gold">
            <Link to={it.to} data-testid={`nav-item-${it.label.toLowerCase().replace(/\s+/g, "-")}`}>
              {it.label}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#0A0B0E]/90 border-b border-smasher-gold/15">
      <nav className="max-w-[1500px] mx-auto px-4 sm:px-6 h-[76px] flex items-center justify-between gap-4">
        <Logo />

        <div className="hidden lg:flex items-center gap-6">
          <NavLink
            to="/"
            className={({ isActive }) =>
              `p-2 rounded-lg transition-colors ${isActive ? "text-smasher-gold" : "text-slate-200 hover:text-smasher-gold"}`
            }
            data-testid="nav-home"
          >
            <Home className="w-5 h-5" />
          </NavLink>
          <NavDropdown label="PICKS" items={PICKS} />
          <NavDropdown label="ODDS" items={ODDS} />
          <NavDropdown label="TOOLS" items={TOOLS} />
          <NavLink to="/news" className="text-sm font-semibold tracking-wide text-slate-200 hover:text-smasher-gold transition-colors" data-testid="nav-blog">
            BLOG
          </NavLink>
          <NavLink to="/leaderboard" className="text-sm font-semibold tracking-wide text-slate-200 hover:text-smasher-gold transition-colors" data-testid="nav-leaderboard">
            LEADERBOARD
          </NavLink>
          <NavLink to="/about" className="text-sm font-semibold tracking-wide text-slate-200 hover:text-smasher-gold transition-colors" data-testid="nav-about">
            ABOUT
          </NavLink>
        </div>

        <div className="hidden lg:flex items-center gap-3">
          {user && user.role === "admin" && (
            <Link to="/admin" className="p-2 text-slate-300 hover:text-smasher-gold" title="Admin" data-testid="nav-admin">
              <LayoutDashboard className="w-5 h-5" />
            </Link>
          )}
          {user && (
            <Link to="/members" className="btn-ghost-gold px-4 py-2 rounded-lg text-sm font-bold" data-testid="nav-members">
              MY AREA
            </Link>
          )}
          {user ? (
            <button onClick={handleLogout} className="btn-ghost-gold px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2" data-testid="nav-logout">
              <LogOut className="w-4 h-4" /> LOGOUT
            </button>
          ) : (
            <Link to="/login" className="btn-ghost-gold px-5 py-2 rounded-lg text-sm font-bold" data-testid="nav-login">
              LOGIN
            </Link>
          )}
          {!(user && (user.role === "admin" || user.membership === "pro")) && (
            <Link to="/membership" className="btn-gold px-5 py-2 rounded-lg text-sm flex items-center gap-2" data-testid="nav-join">
              <Crown className="w-4 h-4" /> {user?.membership === "starter" ? "UPGRADE" : "JOIN NOW"}
            </Link>
          )}
        </div>

        <button className="lg:hidden text-slate-100 p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
          {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden border-t border-smasher-gold/15 bg-[#0A0B0E] px-4 py-4 space-y-1" data-testid="mobile-menu">
          {[{ label: "HOME", to: "/" }, ...PICKS.map((p) => ({ label: p.label, to: p.to })), { label: "NEWS", to: "/news" }, { label: "EDUCATION", to: "/education" }, { label: "STRATEGY", to: "/strategy" }, { label: "EBOOK", to: "/ebook" }, { label: "MEMBERSHIP", to: "/membership" }, { label: "LEADERBOARD", to: "/leaderboard" }, { label: "ABOUT", to: "/about" }, ...(user ? [{ label: "MY AREA", to: "/members" }] : [])].map((it) => (
            <Link key={it.to + it.label} to={it.to} onClick={() => setOpen(false)} className="block py-2.5 font-semibold text-slate-200 hover:text-smasher-gold border-b border-white/5" data-testid={`mobile-nav-${it.label.toLowerCase()}`}>
              {it.label}
            </Link>
          ))}
          <div className="flex gap-3 pt-3">
            {user ? (
              <button onClick={() => { handleLogout(); setOpen(false); }} className="btn-ghost-gold flex-1 py-2.5 rounded-lg font-bold">LOGOUT</button>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="btn-ghost-gold flex-1 py-2.5 rounded-lg font-bold text-center">LOGIN</Link>
            )}
            <Link to="/membership" onClick={() => setOpen(false)} className="btn-gold flex-1 py-2.5 rounded-lg font-bold text-center">JOIN NOW</Link>
          </div>
        </div>
      )}
    </header>
  );
}
